/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { css } from "@emotion/react";
import { useSelector } from "react-redux";
import { Column, Row } from "../../../layout/flexbox";
import * as Buttons from "../../../forms/elements/buttons";
import ErrorView from "../../../forms/ErrorView";
import { IAppState } from "../../../core/redux/IAppState";
import { useAccessRulesEditor } from "../useAccessRulesEditor";
import { getAllValidationErrors } from "../validation/validationUtils";
import { hasAnyDateRangeOverlap } from "../blackoutDateRange/blackoutDateRange";
import { createEmptyEditableEffectiveDateRangeRule } from "../effectiveDateRange/effectiveDateRange";
import { getResource } from "../../../language/resources/getResource";
import { LiftAccessRuleSetEditorLayout } from "./LiftAccessRuleSetEditorLayout";
import { LiftAccessRulesEditor } from "./LiftAccessRulesEditor";
import type { RuleSection } from "./LiftAccessRulesEditor";
import { BulkEditChangeSummary } from "./BulkEditChangeSummary";
import {
  reviewGridHeightPx,
  reviewGridWidthPx,
  scanBulkEditChanges,
} from "./bulkEditChangeScan";
import { ROW_SPACING } from "../../../forms/styles";
import { MediumText } from "../../../styles/styles";
import {
  EditableLiftAccessRules,
  LiftAccessRuleSet,
  liftAccessRuleSetKey,
} from "./models";
import { AccessRuleChangeSet } from "../models";
import { mapAccessRuleBulkChangeRequests } from "../requests/accessRuleBulkChangeRequestMapper";
import { useSearchResultFreshness } from "../../useSearchResultFreshness";
import failureIcon from "../../../assets/icons/failureTriangleRed.svg";
import type { AccessRuleChangeRequest } from "../requests/accessRuleRequestBuilder";
import {
  BLACK,
  COOLER_GREY,
  DARK_GREY,
  DARK_RED_CREEK,
  IKON_BLUE,
  LIGHT_GREY,
  MUTED_GREY,
  RED_CREEK,
  WHITE,
} from "../../../styles/colors";

/** How an edit to one rule category is applied across the selection. */
export type BulkCategoryMode = "unchanged" | "add" | "replace" | "clear";
export type BulkSingletonMode = "unchanged" | "replace" | "clear";
export type BulkEffectiveMode = "unchanged" | "replace";

export type BulkChangeModes = {
  effectiveDate: BulkEffectiveMode;
  effectiveTime: BulkEffectiveMode;
  maxDays: BulkSingletonMode;
  blackoutDateRange: BulkCategoryMode;
  blackoutDayOfWeek: BulkCategoryMode;
};

/**
 * Above this many rule sets the review scan is slow enough to announce. Measured:
 * ~11ms at 100, ~78ms at 1,000, ~300ms at 5,000. Below it the pending state comes
 * and goes inside a frame or two and reads as a flicker.
 */
const ANNOUNCE_REVIEW_ABOVE = 1000;

const INITIAL_MODES: BulkChangeModes = {
  effectiveDate: "unchanged",
  effectiveTime: "unchanged",
  maxDays: "unchanged",
  blackoutDateRange: "unchanged",
  blackoutDayOfWeek: "unchanged",
};

const modePanelCss = css`
  margin: 0 20px 10px;
  border: 1px solid ${COOLER_GREY};
  border-radius: 6px;
  padding: 12px 16px;
`;

const modePanelHintCss = css`
  font-size: 13px;
  color: ${DARK_GREY};
  margin-bottom: 10px;
`;

const modeRowCss = css`
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  column-gap: 32px;
  align-items: baseline;
  padding: 10px 12px;
  border-radius: 4px;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${LIGHT_GREY};
  }

  /* Changed rows go from muted to full-ink bold. Don't use a grey fill instead —
     grey reads as disabled, the opposite of "this will change". */
  .category-label {
    font-size: 14px;
    font-weight: 500;
    color: ${MUTED_GREY};
    transition: color 120ms ease;
  }

  &.is-active .category-label {
    font-weight: 700;
    color: ${BLACK};
  }

  /* Fixed columns keep every option aligned across rows. */
  .options {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 6px 10px;
    min-width: 0;
  }

  .option-slot {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    white-space: nowrap;
    min-height: 20px;
  }

  label.option-slot {
    cursor: pointer;
  }

  .effect-hint {
    grid-column: 2;
    margin-top: 6px;
    font-size: 12px;
    color: ${IKON_BLUE};
  }

  .effect-hint.is-destructive {
    color: ${DARK_RED_CREEK};
    font-weight: 600;
  }
`;

// The dialog's only scroll region: takes the leftover height so the count, mode
// panel and footer stay put. Needs fillCss on the layout to be bounded.
const rulesScrollCss = css`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
`;

// Lets the layout column shrink below its content, so the scroll lands on
// rulesScrollCss instead of the modal root.
const fillCss = css`
  flex: 1 1 auto;
  min-height: 0;
`;

const emptyStateCss = css`
  margin-left: 20px;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 15px;
  color: ${DARK_GREY};
  border: 1px dashed ${COOLER_GREY};
  border-radius: 6px;
`;

const summaryCss = css`
  margin: 10px 20px 12px;
  font-size: 15px;
  font-weight: 600;
  color: ${BLACK};
`;

// Bounded, so the affected-rule-sets grid can take the leftover height and scroll
// inside itself. The overflow here only catches a tall summary with the grid closed.
const reviewCss = css`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  margin: 10px 20px 0;
  overflow-y: auto;
`;

// Thickened so it doesn't read skinny next to the label.
const backArrowCss = css`
  display: inline-block;
  margin-right: 6px;
  font-weight: 700;
  font-size: 16px;
  line-height: 1;
  -webkit-text-stroke: 0.8px currentcolor;
`;

// Sits below the scroll region, so it stays visible.
const footerCss = css`
  ${ROW_SPACING};
  position: sticky;
  bottom: 0;
  margin-top: 12px;
  padding: 12px 20px;
  background: ${WHITE};
  border-top: 1px solid ${LIGHT_GREY};
`;

// In the footer, not a banner above the grid, which would cost the grid two rows.
// Plain text rather than a bordered box: it is not clickable.
const staleWarningCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: auto;
  max-width: 60%;
  ${MediumText};
  font-weight: 600;
  color: ${RED_CREEK};

  img {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
  }
`;

type CategoryKey = keyof BulkChangeModes;

type ModeValue = "unchanged" | "add" | "replace" | "clear";

// Fixed column order. Every row renders all four slots, blanking unsupported
// ones, so an option never shifts position between rows. Don't build per-row
// option lists — that's what breaks the alignment.
const MODE_COLUMNS: { value: ModeValue; labelKey: string }[] = [
  { value: "unchanged", labelKey: "ACCESS_RULE_BULK_MODE_UNCHANGED" },
  { value: "add", labelKey: "ACCESS_RULE_BULK_MODE_ADD" },
  { value: "replace", labelKey: "ACCESS_RULE_BULK_MODE_REPLACE" },
  { value: "clear", labelKey: "ACCESS_RULE_BULK_MODE_CLEAR" },
];

// Singletons can't be added to; effective dates/time are required so can't be
// cleared. These are real constraints, not presentation choices.
const CATEGORY_MODES: Record<CategoryKey, ModeValue[]> = {
  effectiveDate: ["unchanged", "replace"],
  effectiveTime: ["unchanged", "replace"],
  maxDays: ["unchanged", "replace", "clear"],
  blackoutDateRange: ["unchanged", "add", "replace", "clear"],
  blackoutDayOfWeek: ["unchanged", "add", "replace", "clear"],
};

const SINGLETON_CATEGORIES: CategoryKey[] = [
  "effectiveDate",
  "effectiveTime",
  "maxDays",
];

/** Needs rule input below; clear doesn't. */
const isEditorMode = (mode: string) => mode === "add" || mode === "replace";

const timeOfDay = (d: Date) =>
  d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();

/**
 * Validates only the parts being replaced — the rule's own validate() requires all
 * four fields and would reject replacing time alone. Returns a resource key, or null.
 */
const validateEffectiveParts = (
  rule: any,
  dateActive: boolean,
  timeActive: boolean,
): string | null => {
  if (!rule) return "ACCESS_RULE_BULK_EDIT_CATEGORY_EMPTY";

  if (dateActive) {
    if (!rule.effectiveDateStart || !rule.effectiveDateEnd) {
      return "ACCESS_RULE_BULK_EDIT_VALIDATION_ERROR_UNABLE_TO_SAVE";
    }
    if (rule.effectiveDateStart.getTime() > rule.effectiveDateEnd.getTime()) {
      return "ACCESS_RULE_BULK_EDIT_VALIDATION_ERROR_UNABLE_TO_SAVE";
    }
  }

  if (timeActive) {
    if (!rule.effectiveTimeSlotStart || !rule.effectiveTimeSlotEnd) {
      return "ACCESS_RULE_BULK_EDIT_VALIDATION_ERROR_UNABLE_TO_SAVE";
    }
    if (
      timeOfDay(rule.effectiveTimeSlotStart) >=
      timeOfDay(rule.effectiveTimeSlotEnd)
    ) {
      return "ACCESS_RULE_BULK_EDIT_VALIDATION_ERROR_UNABLE_TO_SAVE";
    }
  }

  return null;
};

/**
 * Discards the rules entered for any category whose editor the new modes hide, so a
 * switch back can't silently restore abandoned rows.
 */
export const clearRulesForHiddenEditors = (
  previous: BulkChangeModes,
  next: BulkChangeModes,
  rules: EditableLiftAccessRules,
): EditableLiftAccessRules => {
  const nowHidden = (key: CategoryKey) =>
    isEditorMode(previous[key]) && !isEditorMode(next[key]);

  const effectiveHidden =
    (nowHidden("effectiveDate") || nowHidden("effectiveTime")) &&
    !isEditorMode(next.effectiveDate) &&
    !isEditorMode(next.effectiveTime);

  const maxDaysHidden = nowHidden("maxDays");
  const blackoutDateRangeHidden = nowHidden("blackoutDateRange");
  const blackoutDayOfWeekHidden = nowHidden("blackoutDayOfWeek");

  // Same reference when nothing was hidden: a new object makes the session effect
  // report a draft change on every mode click.
  if (
    !effectiveHidden &&
    !maxDaysHidden &&
    !blackoutDateRangeHidden &&
    !blackoutDayOfWeekHidden
  ) {
    return rules;
  }

  return {
    ...rules,
    effectiveDateRangeRules: effectiveHidden
      ? [createEmptyEditableEffectiveDateRangeRule()]
      : rules.effectiveDateRangeRules,
    maxDaysRules: maxDaysHidden ? [] : rules.maxDaysRules,
    blackoutDateRangeRules: blackoutDateRangeHidden
      ? []
      : rules.blackoutDateRangeRules,
    blackoutDayOfWeekRuleGroups: blackoutDayOfWeekHidden
      ? []
      : rules.blackoutDayOfWeekRuleGroups,
  };
};

/**
 * The gate for entering review. Returns a resource key, or null when the edit is
 * complete enough. Category-empty runs last so a category with rows in it but nothing
 * usable reports the highlighted-rules error instead.
 */
export const getBulkEditValidationErrorKey = (input: {
  modes: BulkChangeModes;
  changeSet: AccessRuleChangeSet | null;
  hasEmptyNonDeletedBlackoutDayOfWeekGroup: boolean | undefined;
  blackoutDateRangeRules: EditableLiftAccessRules["blackoutDateRangeRules"];
}): string | null => {
  const {
    modes,
    changeSet,
    hasEmptyNonDeletedBlackoutDayOfWeekGroup,
    blackoutDateRangeRules,
  } = input;

  const anyCategoryActive = (Object.keys(modes) as CategoryKey[]).some(
    (key) => modes[key] !== "unchanged",
  );
  if (!anyCategoryActive) return "ACCESS_RULE_BULK_EDIT_NO_CHANGES";

  const showEffectiveDate = modes.effectiveDate === "replace";
  const showEffectiveTime = modes.effectiveTime === "replace";

  if (showEffectiveDate || showEffectiveTime) {
    const effectiveError = validateEffectiveParts(
      changeSet?.effectiveDateRange.created[0],
      showEffectiveDate,
      showEffectiveTime,
    );
    if (effectiveError) return effectiveError;
  }

  // Delete all defines no rules, so it's exempt.
  const activeCreated: { key: CategoryKey; created: any[] }[] = [
    {
      key: "maxDays" as CategoryKey,
      created: changeSet?.maxDays.created ?? [],
    },
    {
      key: "blackoutDateRange" as CategoryKey,
      created: changeSet?.blackoutDateRange.created ?? [],
    },
    {
      key: "blackoutDayOfWeek" as CategoryKey,
      created: changeSet?.blackoutDayOfWeek.created ?? [],
    },
  ].filter(({ key }) => isEditorMode(modes[key]));

  if (
    (isEditorMode(modes.blackoutDayOfWeek) &&
      hasEmptyNonDeletedBlackoutDayOfWeekGroup) ||
    (isEditorMode(modes.blackoutDateRange) &&
      hasAnyDateRangeOverlap(blackoutDateRangeRules)) ||
    getAllValidationErrors(activeCreated.map(({ created }) => created)).length >
      0
  ) {
    return "ACCESS_RULE_BULK_EDIT_VALIDATION_ERROR_UNABLE_TO_SAVE";
  }

  // Every Add/Replace category needs at least one rule.
  if (activeCreated.some(({ created }) => created.length === 0)) {
    return "ACCESS_RULE_BULK_EDIT_CATEGORY_EMPTY";
  }

  return null;
};

/** Permanently deletes existing rules on the selected sets. */
const isDestructiveMode = (mode: string) =>
  mode === "replace" || mode === "clear";

/**
 * Everything that must survive this editor being remounted as it moves between the
 * docked modal and a pop-out window. Held by the parent, which doesn't unmount, and
 * fed back in via initialSession.
 */
export type BulkEditSession = {
  draft: { modes: BulkChangeModes; rules: EditableLiftAccessRules };
  confirming: boolean;
  gridOpen: boolean;
  showAllColumns: boolean;
  /** What the review grid needs, or 0 on compose. Only the editor knows both. */
  reviewGridWidth: number;
  reviewGridHeight: number;
  /** The last Review's verdict, so a remount doesn't drop it but keep the edit. */
  submitCount: number;
  hasChangesSinceSubmit: boolean;
  validationError?: string;
};

type Props = {
  ruleSets: LiftAccessRuleSet[];
  onDismiss: () => void;
  /**
   * Hands the built requests to the parent, which owns the run — one request per
   * rule set the scan found will change, never the whole selection.
   */
  onApply?: (saveRequests: AccessRuleChangeRequest[]) => void;
  /** Cancel — lets the parent confirm first. Falls back to onDismiss. */
  onRequestClose?: () => void;
  /** State to restore across a dock / pop-out remount. */
  initialSession?: BulkEditSession;
  /** Reports current state so the parent can preserve it across that remount. */
  onSessionChange?: (session: BulkEditSession) => void;
};

export function LiftAccessRuleSetBulkEditor({
  ruleSets,
  onDismiss,
  onApply,
  onRequestClose,
  initialSession,
  onSessionChange,
}: Props) {
  const resources = useSelector((s: IAppState) => s.locale);
  const userId = useSelector((s: IAppState) => s.userState.userId);
  const operatorId = useSelector((s: IAppState) => s.userState.userName);
  const dateFormat =
    useSelector((s: IAppState) => s.resortToolsConfig?.defaultDateFormat) ??
    "MM/dd/yyyy";
  // The formats the summary renders with. The scan compares displayed values, so it
  // must use these or no-op detection disagrees with what the user reads.
  const scanDateFormat =
    useSelector((s: IAppState) => s.defaultDateFormat) ?? "MM/dd/yyyy";
  const scanTimeFormat =
    useSelector((s: IAppState) => s.defaultTimeFormat) ?? "h:mm a";

  // Hint for the effective date editor; blank unless the whole selection shares
  // one selling season.
  const commonSellingSeason = useMemo(() => {
    const seasons = new Set(
      ruleSets.map((rs) => rs.sellingSeasonDescription).filter(Boolean),
    );
    return seasons.size === 1 ? [...seasons][0] : "";
  }, [ruleSets]);

  const [modes, setModes] = useState<BulkChangeModes>(
    initialSession?.draft.modes ?? INITIAL_MODES,
  );

  // Empty on purpose: a bulk edit defines new values, not any one set's current
  // ones. Don't seed from the selection.
  const [initialRules] = useState({
    effectiveDateRangeRules: [],
    maxDaysRules: [],
    blackoutDateRangeRules: [],
    blackoutDayOfWeekRules: [],
  });

  const {
    editableRules,
    setEditableRules,
    changeSet,
    hasEmptyNonDeletedBlackoutDayOfWeekGroup,
  } = useAccessRulesEditor(initialRules, initialSession?.draft.rules);

  // submitCount drives the rule editors' highlighting — bumping it on a failed
  // Review is what marks the offending rules.
  const [submitCount, setSubmitCount] = useState(
    initialSession?.submitCount ?? 0,
  );
  const [hasChangesSinceSubmit, setHasChangesSinceSubmit] = useState(
    initialSession?.hasChangesSinceSubmit ?? false,
  );
  const [validationError, setValidationError] = useState<string | undefined>(
    initialSession?.validationError,
  );
  // Two steps: compose (edit) → review ("what will change"), preview only.
  const [showReview, setShowReview] = useState(
    initialSession?.confirming ?? false,
  );
  const [isReviewPending, startReviewTransition] = useTransition();

  // Held here rather than in the summary so they survive the remount too.
  const [gridOpen, setGridOpen] = useState(initialSession?.gridOpen ?? false);
  const [showAllColumns, setShowAllColumns] = useState(
    initialSession?.showAllColumns ?? false,
  );

  // Two mode rows over one underlying rule, so the effective section renders
  // once and exposes only the part(s) being replaced.
  const showEffectiveDate = modes.effectiveDate === "replace";
  const showEffectiveTime = modes.effectiveTime === "replace";

  // Derived from modes rather than held as state, so the two can't drift.
  const editorSections = useMemo(() => {
    const sections: RuleSection[] = [];
    if (
      modes.effectiveDate === "replace" ||
      modes.effectiveTime === "replace"
    ) {
      sections.push("effectiveDateRange");
    }
    if (isEditorMode(modes.maxDays)) sections.push("maxDays");
    if (isEditorMode(modes.blackoutDateRange))
      sections.push("blackoutDateRange");
    if (isEditorMode(modes.blackoutDayOfWeek))
      sections.push("blackoutDayOfWeek");
    return sections;
  }, [modes]);

  const anyEditorSection = editorSections.length > 0;

  const changingCategories = useMemo(
    () =>
      (Object.keys(modes) as CategoryKey[]).filter(
        (key) => modes[key] !== "unchanged",
      ),
    [modes],
  );

  const anyCategoryActive = changingCategories.length > 0;

  // Review step only — the expensive pass, and compose has no use for the answer.
  const scan = useMemo(() => {
    if (!showReview || !changeSet) return undefined;
    return scanBulkEditChanges({
      ruleSets,
      activeCategories: changingCategories,
      changeSet,
      modes,
      resources,
      dateFormat: scanDateFormat,
      timeFormat: scanTimeFormat,
    });
  }, [
    showReview,
    changeSet,
    ruleSets,
    changingCategories,
    modes,
    resources,
    scanDateFormat,
    scanTimeFormat,
  ]);

  // A number, not the scan: the effect below re-runs on a height change, not a rebuild.
  // 0 while collapsed, so opening and closing the grid re-fits the window.
  const reviewGridHeight = useMemo(
    () => (showReview && gridOpen && scan ? reviewGridHeightPx(scan) : 0),
    [showReview, gridOpen, scan],
  );

  const rulesScrollRef = useRef<HTMLDivElement | null>(null);

  // A ref, so the parent can pass a fresh closure without re-running the effect.
  const onSessionChangeRef = useRef(onSessionChange);
  onSessionChangeRef.current = onSessionChange;

  useEffect(() => {
    onSessionChangeRef.current?.({
      draft: { modes, rules: editableRules },
      confirming: showReview,
      gridOpen,
      showAllColumns,
      // 0 on compose — no grid to size to.
      reviewGridWidth: showReview
        ? reviewGridWidthPx(changingCategories, showAllColumns)
        : 0,
      reviewGridHeight,
      submitCount,
      hasChangesSinceSubmit,
      validationError,
    });
  }, [
    modes,
    editableRules,
    showReview,
    gridOpen,
    showAllColumns,
    changingCategories,
    reviewGridHeight,
    submitCount,
    hasChangesSinceSubmit,
    validationError,
  ]);

  // Clears the error: it described the edit as it was when Review was pressed.
  const onAnyEdit = () => {
    setHasChangesSinceSubmit(true);
    setValidationError(undefined);
  };

  const setMode = (category: CategoryKey, value: string) => {
    const next = { ...modes, [category]: value } as BulkChangeModes;
    setModes(next);
    setEditableRules((rules) => clearRulesForHiddenEditors(modes, next, rules));
    setValidationError(undefined);
  };

  /** True when the edit is complete enough to review; sets an inline error if not. */
  const validateForReview = (): boolean => {
    setSubmitCount((c) => c + 1);
    setHasChangesSinceSubmit(false);

    const errorKey = getBulkEditValidationErrorKey({
      modes,
      changeSet,
      hasEmptyNonDeletedBlackoutDayOfWeekGroup,
      blackoutDateRangeRules: editableRules.blackoutDateRangeRules,
    });

    if (errorKey) {
      setValidationError(getResource(resources, errorKey));
      return false;
    }

    setValidationError(undefined);
    return true;
  };

  const handleReviewClick = () => {
    if (!validateForReview()) return;
    // Reported as pending, so a large selection doesn't look like a dead click.
    startReviewTransition(() => setShowReview(true));
  };

  // Read from the store, not props: showModalAction freezes props at open time, and
  // this also has to work once the dialog is popped out into its own window.
  const { resultsStale } = useSearchResultFreshness();

  // Skip the discard confirmation when stale: "Keep editing" would invite the user
  // back into a dialog that can no longer apply anything.
  const handleClose = resultsStale ? onDismiss : (onRequestClose ?? onDismiss);

  // Any Replace or Delete all deletes or overwrites existing rules, which escalates
  // the commit button to destructive styling.
  const hasDestructive = changingCategories.some((key) =>
    isDestructiveMode(modes[key]),
  );

  // What the commit button counts and what gets sent — usually fewer than the
  // selection, since some sets already match.
  const changedRuleSetCount = scan?.changedKeys.size ?? 0;

  // The edit changes nothing (e.g. Delete all max days when every set is already
  // unlimited), so the commit button becomes Close. Only known once the scan runs.
  const noChanges = !!scan && changedRuleSetCount === 0;

  const handleApply = () => {
    // Without a scan, which rule sets change isn't known — never guess.
    if (!scan || !changeSet || !onApply) return;

    const ruleSetsToApply = ruleSets.filter((rs) =>
      scan.changedKeys.has(liftAccessRuleSetKey(rs)),
    );
    if (ruleSetsToApply.length === 0) return;

    onApply(
      mapAccessRuleBulkChangeRequests({
        userId,
        operatorId,
        ruleSets: ruleSetsToApply,
        changeSet,
        modes,
      }),
    );
  };

  // Effective dates/time get their own hints, noting the other part is kept.
  const EFFECTIVE_REPLACE_HINT_KEYS: Partial<Record<CategoryKey, string>> = {
    effectiveDate: "ACCESS_RULE_BULK_MODE_REPLACE_HINT_EFFECTIVE_DATE",
    effectiveTime: "ACCESS_RULE_BULK_MODE_REPLACE_HINT_EFFECTIVE_TIME",
  };

  const renderEffectHint = (category: CategoryKey) => {
    const mode = modes[category];
    const isSingleton = SINGLETON_CATEGORIES.includes(category);
    if (mode === "add") {
      return (
        <div className="effect-hint">
          {getResource(resources, "ACCESS_RULE_BULK_MODE_ADD_HINT")}
        </div>
      );
    }
    if (mode === "replace") {
      const effectiveHintKey = EFFECTIVE_REPLACE_HINT_KEYS[category];
      return (
        <div className="effect-hint is-destructive">
          {getResource(
            resources,
            effectiveHintKey ??
              (isSingleton
                ? "ACCESS_RULE_BULK_MODE_REPLACE_HINT_SINGLETON"
                : "ACCESS_RULE_BULK_MODE_REPLACE_HINT"),
            ruleSets.length,
          )}
        </div>
      );
    }
    if (mode === "clear") {
      return (
        <div className="effect-hint is-destructive">
          {getResource(
            resources,
            "ACCESS_RULE_BULK_MODE_CLEAR_HINT",
            ruleSets.length,
          )}
        </div>
      );
    }
    return null;
  };

  const renderModeRow = (category: CategoryKey, labelKey: string) => {
    const allowed = CATEGORY_MODES[category];
    const mode = modes[category];
    const rowClass = [
      mode !== "unchanged" && "is-active",
      isDestructiveMode(mode) && "is-destructive",
    ]
      .filter(Boolean)
      .join(" ");
    const labelId = `bulk-cat-${category}`;
    return (
      <div css={modeRowCss} className={rowClass || undefined}>
        <span className="category-label" id={labelId}>
          {getResource(resources, labelKey)}
        </span>
        <div className="options" role="radiogroup" aria-labelledby={labelId}>
          {MODE_COLUMNS.map((column) => {
            if (!allowed.includes(column.value)) {
              // Blank slot holds the column position.
              return (
                <span
                  key={column.value}
                  className="option-slot"
                  aria-hidden="true"
                />
              );
            }
            const inputId = `bulk-mode-${category}-${column.value}`;
            return (
              <label
                key={column.value}
                className="option-slot"
                htmlFor={inputId}
              >
                <input
                  id={inputId}
                  type="radio"
                  name={`bulk-mode-${category}`}
                  value={column.value}
                  checked={mode === column.value}
                  onChange={() => setMode(category, column.value)}
                />
                {getResource(resources, column.labelKey)}
              </label>
            );
          })}
        </div>
        {renderEffectHint(category)}
      </div>
    );
  };

  return (
    <>
      {!showReview && (
        <LiftAccessRuleSetEditorLayout
          autoHeight
          rootCss={fillCss}
          header={
            <Column>
              <div css={summaryCss}>
                {getResource(
                  resources,
                  "ACCESS_RULE_BULK_EDIT_AFFECTED",
                  ruleSets.length,
                )}
              </div>

              <div css={modePanelCss}>
                <div css={modePanelHintCss}>
                  {getResource(resources, "ACCESS_RULE_BULK_EDIT_APPLY_MODE")}
                </div>
                {renderModeRow(
                  "effectiveDate",
                  "ACCESS_RULE_BULK_CATEGORY_EFFECTIVE_DATE",
                )}
                {renderModeRow(
                  "effectiveTime",
                  "ACCESS_RULE_BULK_CATEGORY_EFFECTIVE_TIME",
                )}
                {renderModeRow("maxDays", "ACCESS_RULE_BULK_CATEGORY_MAX_DAYS")}
                {renderModeRow(
                  "blackoutDateRange",
                  "ACCESS_RULE_BULK_CATEGORY_BLACKOUT_RANGE",
                )}
                {renderModeRow(
                  "blackoutDayOfWeek",
                  "ACCESS_RULE_BULK_CATEGORY_BLACKOUT_DOW",
                )}
              </div>
            </Column>
          }
          rules={
            <div css={rulesScrollCss} ref={rulesScrollRef}>
              {anyEditorSection ? (
                <LiftAccessRulesEditor
                  rules={editableRules}
                  sellingSeason={commonSellingSeason}
                  resources={resources}
                  dateFormat={dateFormat}
                  submitCount={submitCount}
                  hasChangesSinceSubmit={hasChangesSinceSubmit}
                  onAnyEdit={onAnyEdit}
                  onRulesChange={setEditableRules}
                  sections={editorSections}
                  effectiveShow={{
                    date: showEffectiveDate,
                    time: showEffectiveTime,
                  }}
                  // Drops the editor's fixed 565px scroll region — rulesScrollCss
                  // owns the scroll, so sections grow naturally.
                  rootCss={css`
                    height: auto;
                    overflow: visible;
                  `}
                />
              ) : (
                <div css={emptyStateCss}>
                  {getResource(
                    resources,
                    // All set to Delete all: nothing to enter, so don't say "set one
                    // to Add or Replace".
                    anyCategoryActive
                      ? "ACCESS_RULE_BULK_EDIT_CLEAR_ONLY_STATE"
                      : "ACCESS_RULE_BULK_EDIT_EMPTY_STATE",
                  )}
                </div>
              )}
            </div>
          }
        />
      )}

      {showReview && changeSet && scan && (
        <div css={reviewCss}>
          <BulkEditChangeSummary
            ruleSets={ruleSets}
            changeSet={changeSet}
            modes={modes}
            activeCategories={changingCategories}
            scan={scan}
            gridOpen={gridOpen}
            onGridOpenChange={setGridOpen}
            showAllColumns={showAllColumns}
            onShowAllColumnsChange={setShowAllColumns}
          />
        </div>
      )}

      <ErrorView error={validationError} />

      <Row justifyContent="flex-end" rootCss={footerCss}>
        {/* Beside the buttons it disables, so the reason is where the click is. */}
        {resultsStale && (
          <span css={staleWarningCss}>
            <img src={failureIcon} alt="" aria-hidden="true" />
            {getResource(
              resources,
              "ACCESS_RULE_BULK_EDIT_RESULTS_STALE_SHORT",
            )}
          </span>
        )}
        {showReview ? (
          <>
            <Buttons.BigSecondary
              title={getResource(resources, "ACCESS_RULE_BULK_EDIT_BACK_TITLE")}
              onClick={() => setShowReview(false)}
            >
              <span css={backArrowCss}>←</span>
              {getResource(resources, "ACCESS_RULE_BULK_EDIT_BACK")}
            </Buttons.BigSecondary>

            {noChanges ? (
              // Nothing to apply — offer the way out rather than "Apply to 0".
              <Buttons.BigNeutral
                title={getResource(
                  resources,
                  "ACCESS_RULE_BULK_EDIT_CLOSE_TITLE",
                )}
                onClick={handleClose}
              >
                {getResource(resources, "ACCESS_RULE_BULK_EDIT_CLOSE")}
              </Buttons.BigNeutral>
            ) : hasDestructive ? (
              <Buttons.BigNegative
                title={getResource(
                  resources,
                  "ACCESS_RULE_BULK_EDIT_APPLY_TO_COUNT_TITLE",
                  changedRuleSetCount,
                )}
                onClick={handleApply}
                disabled={resultsStale}
              >
                {getResource(
                  resources,
                  "ACCESS_RULE_BULK_EDIT_APPLY_TO_COUNT",
                  changedRuleSetCount,
                )}
              </Buttons.BigNegative>
            ) : (
              <Buttons.BigNeutral
                title={getResource(
                  resources,
                  "ACCESS_RULE_BULK_EDIT_APPLY_TO_COUNT_TITLE",
                  changedRuleSetCount,
                )}
                onClick={handleApply}
                disabled={resultsStale}
              >
                {getResource(
                  resources,
                  "ACCESS_RULE_BULK_EDIT_APPLY_TO_COUNT",
                  changedRuleSetCount,
                )}
              </Buttons.BigNeutral>
            )}
          </>
        ) : (
          <>
            <Buttons.BigSecondary
              title={getResource(resources, "ACCESS_RULE_MODAL_CANCEL_TITLE")}
              onClick={handleClose}
            >
              {getResource(resources, "ACCESS_RULE_MODAL_CANCEL")}
            </Buttons.BigSecondary>

            <Buttons.BigNeutral
              title={getResource(
                resources,
                "ACCESS_RULE_BULK_EDIT_REVIEW_TITLE",
              )}
              onClick={handleReviewClick}
              disabled={!anyCategoryActive || isReviewPending || resultsStale}
            >
              {getResource(
                resources,
                isReviewPending && ruleSets.length > ANNOUNCE_REVIEW_ABOVE
                  ? "ACCESS_RULE_BULK_EDIT_REVIEW_PENDING"
                  : "ACCESS_RULE_BULK_EDIT_REVIEW",
              )}
            </Buttons.BigNeutral>
          </>
        )}
      </Row>
    </>
  );
}
